import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import ListGroup from 'react-bootstrap/ListGroup';
import Badge from 'react-bootstrap/Badge';
import Card from 'react-bootstrap/Card';
import { BsCheckCircleFill } from 'react-icons/bs';

import { previewImportWords, confirmImportWords } from '../services/importsService';

import AlertMessage from '../components/AlertMessage';

const PageHeader = styled.h2`
  color: #22223b;
  letter-spacing: 0.01em;
  font-size: 1.5rem;
  font-weight: 500;
  text-align: center;
  margin-bottom: 1.5rem;
`;

const StepsIndicator = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
  gap: 1.5rem;
`;

const Step = styled.div`
  font-size: 0.95rem;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  color: ${({ $active }) => ($active ? '#3a3a6a' : '#b0b0b0')};
  border-bottom: 2px solid ${({ $active }) => ($active ? '#8e3dff' : 'transparent')};
  padding-bottom: 2px;
  transition:
    color 0.2s,
    border-bottom 0.2s;
`;

const ItemCounter = styled.div`
  text-align: right;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  color: ${({ $error }) => ($error ? '#d90429' : '#888')};
  font-weight: 400;
`;

const Instructions = styled.p`
  padding: 5px;
  margin: 0;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
`;

const ActionsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;

  @media (min-width: 600px) {
    flex-direction: row;
    justify-content: flex-end;
    gap: 1rem;
  }
`;

const BlockButton = styled(Button)`
  flex: 1 1 0;
  min-width: 120px;
  font-size: 0.9rem;
  padding: 0.3rem 0.75rem;
`;

const ImportWordsPage = ({ userId, getAccessToken }) => {
  const [step, setStep] = useState('input'); // input | preview | processing | confirm
  const [text, setText] = useState('');
  const [preview, setPreview] = useState([]);
  const [processingResult, setProcessingResult] = useState(null);
  const [alert, setAlert] = useState({ message: '', variant: 'danger' });
  const navigate = useNavigate();

  const showAlert = (message, variant = 'danger', duration = 5000) => {
    setAlert({ message, variant });
    if (duration > 0) setTimeout(() => setAlert({ message: '', variant }), duration);
  };

  const stepIndex = {
    input: 0,
    preview: 1,
    processing: 2,
    done: 2,
  }[step];

  const steps = ['Paste', 'Preview', 'Import'];
  const MAX_ITEMS = 200;
  const WARNING_ITEMS = 50;

  const items = text
    .split(/[\n,]+/)
    .map((w) => w.trim())
    .filter(Boolean);
  const itemCount = items.length;
  const overLimit = itemCount > MAX_ITEMS;
  const showWarning = itemCount > WARNING_ITEMS;

  // Step 1: Input
  const handlePreview = async (e) => {
    e.preventDefault();
    if (!getAccessToken) {
      showAlert('User session is not ready. Please try again.');
      return;
    }
    try {
      const res = await previewImportWords({ text, lang: 'en' }, getAccessToken);
      if (res.data.preview) {
        setPreview(res.data.preview);
        setStep('preview');
      } else {
        showAlert('Failed to connect. Please try again.');
        setStep('input');
      }
    } catch (err) {
      showAlert('Failed to connect. Please try again.');
      setStep('input');
    }
  };

  // Step 2: Confirm Import
  const handleConfirm = async () => {
    if (!getAccessToken) {
      showAlert('User session is not ready. Please try again.');
      return;
    }
    setStep('processing');
    try {
      const res = await confirmImportWords(
        {
          words: preview,
          metadata: { platform: 'web', source: 'import' },
        },
        getAccessToken,
      );
      setProcessingResult(res.data);
      setStep('done');
    } catch (err) {
      showAlert('Failed to import words.');
      setStep('preview');
    }
  };

  // Step 3: Go back to input
  const handleBack = () => setStep('input');

  // Step 4: Go to library
  const handleGoToLibrary = () => navigate('/words');

  return (
    <div>
      <PageHeader>Import</PageHeader>
      <StepsIndicator>
        {steps.map((label, idx) => (
          <Step key={label} $active={stepIndex === idx}>
            {label}
          </Step>
        ))}
      </StepsIndicator>
      <AlertMessage message={alert.message} variant={alert.variant} />

      {step === 'input' && (
        <Form onSubmit={handlePreview}>
          <Form.Group>
            <Instructions>
              Paste your list here, please add a comma or a new line between each item.
            </Instructions>
            <Form.Control
              as='textarea'
              rows={12}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={`british shorthair\nborder collie`}
            />
            {showWarning && (
              <ItemCounter $error={overLimit}>
                {itemCount} / {MAX_ITEMS} items
              </ItemCounter>
            )}
          </Form.Group>
          <ActionsRow>
            <BlockButton type='submit' variant='primary' disabled={overLimit}>
              Preview
            </BlockButton>
            <BlockButton type='button' variant='outline-secondary' onClick={handleGoToLibrary}>
              Cancel
            </BlockButton>
          </ActionsRow>
        </Form>
      )}

      {step === 'processing' && (
        <div className='text-center mb-4'>
          <Spinner
            animation='border'
            style={{ width: 48, height: 48, color: '#8e3dff', marginBottom: 8 }}
            role='status'
          />
          <h4 style={{ marginTop: 8, marginBottom: 8 }}>Processing</h4>
        </div>
      )}

      {step === 'preview' && (
        <div>
          <Instructions>
            Please review your list below.
            {preview.some((item) => item.status === 'existing') && (
              <>
                {' '}
                Words already in your library are marked as{' '}
                <Badge pill bg='success'>
                  Added
                </Badge>
              </>
            )}
          </Instructions>
          <Card className='mb-3'>
            <Card.Body style={{ padding: '1rem' }}>
              <ListGroup style={{ border: 'none' }}>
                {preview.map((item) => (
                  <ListGroup.Item
                    key={item.word}
                    style={{
                      border: 'none',
                      borderRadius: 0,
                      paddingLeft: 0,
                      paddingRight: 0,
                      background: 'transparent',
                      paddingTop: '0.25rem',
                      paddingBottom: '0.25rem',
                      minHeight: 'unset',
                      lineHeight: 1,
                    }}
                  >
                    {item.word}{' '}
                    {item.status === 'existing' && (
                      <Badge pill bg='success'>
                        Added
                      </Badge>
                    )}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>
          <ActionsRow>
            <BlockButton variant='primary' onClick={handleConfirm}>
              Import
            </BlockButton>
            <BlockButton variant='outline-secondary' onClick={handleBack}>
              Back
            </BlockButton>
          </ActionsRow>
        </div>
      )}

      {step === 'done' && processingResult && (
        <div>
          <div className='text-center mb-4'>
            <BsCheckCircleFill size={48} color='#38b000' style={{ marginBottom: 8 }} />
            <h4 style={{ marginTop: 8, marginBottom: 8 }}>Import Complete</h4>
            {processingResult.results.filter((r) => r.status === 'added' && r.type === 'search')
              .length > 0 && (
              <div style={{ fontSize: '0.8rem', color: '#222', marginBottom: 2 }}>
                {
                  processingResult.results.filter(
                    (r) => r.status === 'added' && r.type === 'search',
                  ).length
                }{' '}
                word
                {processingResult.results.filter((r) => r.status === 'added' && r.type === 'search')
                  .length > 1
                  ? 's'
                  : ''}
              </div>
            )}
            {processingResult.results.filter((r) => r.status === 'added' && r.type === 'custom')
              .length > 0 && (
              <div style={{ fontSize: '0.8rem', color: '#222', marginBottom: 2 }}>
                {
                  processingResult.results.filter(
                    (r) => r.status === 'added' && r.type === 'custom',
                  ).length
                }{' '}
                custom phrase
                {processingResult.results.filter((r) => r.status === 'added' && r.type === 'custom')
                  .length > 1
                  ? 's'
                  : ''}
              </div>
            )}
          </div>

          {processingResult.results.some((r) => r.status === 'added' && r.type === 'search') && (
            <Card className='mb-3'>
              <Card.Header as='h5' style={{ fontSize: '0.85rem', color: '#555' }}>
                Words
              </Card.Header>
              <Card.Body>
                <ListGroup style={{ border: 'none' }}>
                  {processingResult.results
                    .filter((r) => r.status === 'added' && r.type === 'search')
                    .map((r) => (
                      <ListGroup.Item
                        key={r.word}
                        style={{
                          border: 'none',
                          borderRadius: 0,
                          paddingLeft: 0,
                          paddingRight: 0,
                          background: 'transparent',
                          paddingTop: '0.25rem',
                          paddingBottom: '0.25rem',
                          minHeight: 'unset',
                          lineHeight: 1,
                        }}
                      >
                        {r.word}
                      </ListGroup.Item>
                    ))}
                </ListGroup>
              </Card.Body>
            </Card>
          )}

          {processingResult.results.some((r) => r.status === 'added' && r.type === 'custom') && (
            <Card className='mb-3'>
              <Card.Header as='h5' style={{ fontSize: '0.85rem', color: '#555' }}>
                Custom phrases
              </Card.Header>
              <Card.Body>
                <ListGroup style={{ border: 'none' }}>
                  {processingResult.results
                    .filter((r) => r.status === 'added' && r.type === 'custom')
                    .map((r) => (
                      <ListGroup.Item
                        key={r.word}
                        style={{
                          border: 'none',
                          borderRadius: 0,
                          paddingLeft: 0,
                          paddingRight: 0,
                          background: 'transparent',
                          paddingTop: '0.25rem',
                          paddingBottom: '0.25rem',
                          minHeight: 'unset',
                          lineHeight: 1,
                        }}
                      >
                        {r.word}
                      </ListGroup.Item>
                    ))}
                </ListGroup>
                <div style={{ fontSize: '0.8rem', color: '#888', marginTop: 8 }}>
                  <em>Definitions for custom phrases coming soon.</em>
                </div>
              </Card.Body>
            </Card>
          )}

          <ActionsRow>
            <BlockButton variant='primary' onClick={handleGoToLibrary}>
              Go to My Words
            </BlockButton>
          </ActionsRow>
        </div>
      )}
    </div>
  );
};

export default ImportWordsPage;
